import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid"
import { classNames } from "../../../../utils/classNames"
import { DateRangePickerError, DateRangePickerValue } from "../types"
import {
    stripDateInput,
    FORMATTED_DATE_FORMAT,
    getFormattedDateRangeValue,
    getInputDaysLabel,
    isDateRangeValid,
    ISO_DATE_FORMAT,
    moveDateRange,
    parseDateFromInput,
} from "../utils"
import React, { useEffect, useState } from "react"
import { differenceInDays, format, parse } from "date-fns"
import { IsoDate } from "@figurepos/fgr-lib-business-logic/lib/common/dateTime/types"
import { WeekStartDay } from "../../../screens/settings/types"

type Props = {
    value: DateRangePickerValue
    error: DateRangePickerError | null
    minDate: IsoDate | null
    maxDate: IsoDate | null
    weekStartDay: WeekStartDay
    maxRange?: number | null
    onChange: (value: DateRangePickerValue) => void
    onError: (error: DateRangePickerError | null) => void
}

export const CalendarDateInput = (props: Props) => {
    const { start: startFormatted, end: endFormatted } = getFormattedDateRangeValue(props.value)
    const [startInputValue, setStartInputValue] = useState<string>(startFormatted ?? "")
    const [endInputValue, setEndInputValue] = useState<string>(endFormatted ?? "")
    const hasError = !!(props.error?.start || props.error?.end)

    useEffect(() => {
        if (startFormatted) {
            setStartInputValue(startFormatted)
        }
    }, [startFormatted])

    useEffect(() => {
        if (endFormatted) {
            setEndInputValue(endFormatted)
        }
    }, [endFormatted])

    const moveRangeArrowClicked = (direction: "back" | "forward") => {
        const newDateRange = moveDateRange(direction, props.value, props.minDate, props.maxDate)
        if (!newDateRange || !newDateRange.start || !newDateRange.end) {
            return
        }

        props.onChange({ start: newDateRange.start, end: newDateRange.end })
        props.onError({ start: "", end: "" })

        const newStartDate = parse(newDateRange.start, ISO_DATE_FORMAT, new Date())
        const newEndDate = parse(newDateRange.end, ISO_DATE_FORMAT, new Date())

        setStartInputValue(format(newStartDate, FORMATTED_DATE_FORMAT))
        setEndInputValue(format(newEndDate, FORMATTED_DATE_FORMAT))
    }

    const inputOnChange = (input: "start" | "end", value: string) => {
        const formatted = stripDateInput(value)
        if (input === "start") {
            setStartInputValue(formatted)
        } else {
            setEndInputValue(formatted)
        }
    }

    const inputOnBlur = (input: "start" | "end") => {
        const inputValue = input === "start" ? startInputValue : endInputValue

        // If input is empty, show error
        if (!inputValue) {
            if (input === "start") {
                props.onError({ start: "'Start date' cannot be empty.", end: props.error?.end })
                props.onChange({ start: null, end: props.value.end })
            } else {
                props.onError({ start: props.error?.start, end: "'End date' cannot be empty." })
                props.onChange({ start: props.value.start, end: null })
            }
            return
        }

        try {
            let date = parseDateFromInput(inputValue, props.weekStartDay)
            let isoDate = date ? format(date, ISO_DATE_FORMAT) : null

            // If input date string could not be parsed, throw error
            if (!date || !isoDate) {
                throw new Error("Invalid date")
            }

            // If the date is before 2012, set it to 2012
            const minDate = new Date("2012-01-01")
            if (date < minDate) {
                date = minDate
                isoDate = format(date, ISO_DATE_FORMAT)
            }
            // If the date is in the future, set it to today
            const maxDate = new Date()
            if (date > maxDate) {
                date = maxDate
                isoDate = format(date, ISO_DATE_FORMAT)
            }

            const formattedDate = format(date, FORMATTED_DATE_FORMAT)
            if (input === "start") {
                // Validate whether 'Start' date is before 'End' date
                if (!isDateRangeValid(isoDate, props.value.end)) {
                    props.onError({ start: "'Start date' must be before 'End date'.", end: props.error?.end })
                    setStartInputValue(formattedDate)
                    props.onChange({ start: isoDate, end: props.value.end })
                    return
                }

                // 'Start' and 'End' cannot be more than 'maxRange' days apart
                if (props.value.end && props.maxRange) {
                    const endDate = parse(props.value.end, ISO_DATE_FORMAT, new Date())
                    if (Math.abs(differenceInDays(date, endDate)) >= props.maxRange) {
                        props.onError({
                            start: `Maximum date range is ${props.maxRange} days.`,
                            end: props.error?.end,
                        })
                        setStartInputValue(formattedDate)
                        props.onChange({ start: isoDate, end: props.value.end })
                        return
                    }
                }
            }

            if (input === "end") {
                // Validate whether 'End' date is after 'Start' date
                if (!isDateRangeValid(props.value.start, isoDate)) {
                    props.onError({ end: "'End date' must be after 'Start date'.", start: props.error?.start })

                    setEndInputValue(formattedDate)
                    props.onChange({ start: props.value.start, end: isoDate })
                    return
                }

                // 'Start' and 'End' cannot be more than 'maxRange' days apart
                if (props.value.start && props.maxRange) {
                    const startDate = parse(props.value.start, ISO_DATE_FORMAT, new Date())
                    if (Math.abs(differenceInDays(date, startDate)) >= props.maxRange) {
                        props.onError({
                            start: props.error?.start,
                            end: `Maximum date range is ${props.maxRange} days.`,
                        })
                        setEndInputValue(formattedDate)
                        props.onChange({ start: props.value.start, end: isoDate })
                        return
                    }
                }
            }

            // If all validations passed, update the values
            if (input === "start") {
                props.onChange({ start: isoDate, end: props.value.end })
                setStartInputValue(formattedDate)
                props.onError({ start: "", end: "" })
            }

            if (input === "end") {
                props.onChange({ start: props.value.start, end: isoDate })
                setEndInputValue(formattedDate)
                props.onError({ start: "", end: "" })
            }
        } catch {
            // If any error occurred in the 'try' (e.g. because of parsing), show error
            if (input === "start") {
                props.onError({ start: "'Start date' is invalid.", end: props.error?.end })
                props.onChange({ start: null, end: props.value.end })
            }
            if (input === "end") {
                props.onError({ start: props.error?.start, end: "'End date' is invalid." })
                props.onChange({ start: props.value.start, end: null })
            }
        }
    }

    const dateRangeLabel = getInputDaysLabel(props.value, props.error)

    return (
        <div>
            <div className={classes.wrap}>
                <button
                    type="button"
                    onClick={() => moveRangeArrowClicked("back")}
                    disabled={hasError}
                    className={classNames(classes.arrowButton, hasError && classes.arrowButtonDisabled)}>
                    <ChevronLeftIcon className={classes.arrowIconLeft} />
                </button>
                <div className={classes.inputWrap}>
                    <div className={classes.dateRangeLabelWrap}>
                        Dates: <span className={classes.textPrimary}>{dateRangeLabel ?? "-"}</span>
                    </div>
                    <div className={classes.inputWrap2}>
                        <div className="relative">
                            <input
                                className={classNames(classes.dateInput)}
                                value={startInputValue}
                                onChange={e => inputOnChange("start", e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === "Enter") {
                                        inputOnBlur("start")
                                    }
                                }}
                                onBlur={() => inputOnBlur("start")}
                                onFocus={e => {
                                    e.target.select()
                                }}
                                placeholder="MM/DD/YYYY"
                                maxLength={10}
                            />
                            {props.error?.start ? <div className={classes.inputErrorUnderline} /> : null}
                        </div>
                        <div className={classes.separator}>-</div>
                        <div className="relative">
                            <input
                                className={classNames(classes.dateInput)}
                                value={endInputValue}
                                onChange={e => inputOnChange("end", e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === "Enter") {
                                        inputOnBlur("end")
                                    }
                                }}
                                onBlur={() => inputOnBlur("end")}
                                onFocus={e => {
                                    e.target.select()
                                }}
                                placeholder="MM/DD/YYYY"
                                maxLength={10}
                            />
                            {props.error?.end ? <div className={classes.inputErrorUnderline} /> : null}
                        </div>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => moveRangeArrowClicked("forward")}
                    disabled={hasError}
                    className={classNames(classes.arrowButton, hasError && classes.arrowButtonDisabled)}>
                    <ChevronRightIcon className={classes.arrowIconRight} />
                </button>
            </div>
            {props.error?.start || props.error?.end ? (
                <div className={classes.errorWrap}>
                    {props.error?.start ? <div>{props.error.start}</div> : null}
                    {props.error?.end ? <div>{props.error.end}</div> : null}
                </div>
            ) : null}
        </div>
    )
}

const classes = {
    wrap: "w-full h-[46px] flex items-center border border-gray-300 rounded-[4px] bg-white hover:[&:not(.arrow-button)]:border-primary-color",
    arrowButton:
        "arrow-button h-full p-0 m-0 px-1.5 flex items-center justify-center cursor-pointer bg-transparent hover:text-primary-color transition outline-none",
    arrowButtonDisabled: "opacity-40 pointer-events-none",
    arrowIconLeft: "w-[20px] h-auto -mt-px ml-[-2px]",
    arrowIconRight: "w-[20px] h-auto mr-[-2px] -mt-px",
    separator: "-mt-px mr-3",
    inputWrap: "px-2 border-x border-gray-200 flex-1",
    inputWrap2: "flex items-center leading-none text-[14px] font-bold",
    dateInput:
        "bg-transparent block w-[104px] py-0.5 px-1 -ml-1 focus:outline-none font-bold text-black/80 tracking-[0.1px]",
    inputErrorUnderline: "absolute h-[2px] left-[-2px] right-[5px] bottom-[-4px] bg-red-500 w-auto",
    errorWrap: "absolute text-red-500 text-[12px] leading-none mb-0.5 mt-1 pl-0.5",
    dateRangeLabelWrap: "text-black/80 text-[11px] leading-none mt-[3px] mb-[2px]",
    textPrimary: "text-primary-color",
}
